import axios from 'axios';
import { redirectToExpiredLogin } from './session';

// URL fragments allowed to return 401 without triggering a forced logout, even
// if a session token happens to be present: the login endpoint itself — wrong
// password legitimately returns 401 and must show its own inline error, not
// bounce through a "session expired" redirect (which would hide the real
// error / cause a loop).
const EXEMPT_URL_FRAGMENTS = ['/login'];

function isExemptUrl(url) {
  const target = url || '';
  return EXEMPT_URL_FRAGMENTS.some((fragment) => fragment && target.includes(fragment));
}

// Only treat a 401 as "your session expired" when the user actually had a
// session token at the time. This naturally excludes pre-auth flows (create
// account, forgot password, reset-password-via-emailed-link) without needing
// to enumerate every one of their endpoints individually — those pages never
// had a token to begin with, so a 401 from them isn't a session expiry, and
// each already renders its own inline error for that case.
function hadActiveSession() {
  return !!sessionStorage.getItem('token');
}

let installed = false;

// Installs global 401 handling across BOTH request mechanisms this app uses:
//   - axios: CreateAccount, ForgetPassword, ResetPassword, ResetPasswordAfterLogin,
//     UpdateEntitlement, UserApproval, UploadSecurity, ...
//   - window.fetch: Summary, Risk, Holdings, StressScenarios, Historical,
//     Alternatives, SecurityLevel, Settings, WhatIfAnalysis, Portfolios — these
//     pages call r.json() on the response without checking status at all, so
//     an expired-token 401 error body was silently being treated as real data.
// Installed once at app startup, so no individual page needs to change.
export function installSessionExpiryInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const url = error.config?.url || '';
      if (status === 401 && hadActiveSession() && !isExemptUrl(url)) {
        redirectToExpiredLogin();
      }
      return Promise.reject(error);
    }
  );

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url;
    if (response.status === 401 && hadActiveSession() && !isExemptUrl(requestUrl)) {
      redirectToExpiredLogin();
    }
    return response;
  };
}
